import { enqueueSnackbar } from 'notistack';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';

import { fDate } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { useGetEmployerReplacementsQuery, useDeleteEmployerReplacementMutation } from 'src/services/presentations/presentations.service';

import Iconify from 'src/components/iconify';

/**
 * The agency's employer wording for anonymised presentations (JT1-1887), on Settings → Brand. Added
 * from the anonymisation review in the builder; admins can remove entries here.
 */
export default function EmployerReplacementsCard({ canManage }: { canManage: boolean }) {
  const { t } = useTranslate();
  const { currentData: rows = [], isFetching } = useGetEmployerReplacementsQuery();
  const [remove] = useDeleteEmployerReplacementMutation();

  const handleRemove = async (id: string) => {
    try {
      await remove(id).unwrap();
      enqueueSnackbar(t('presentations.employers.removed'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.templates.failed'), { variant: 'error' });
    }
  };

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader title={t('presentations.employers.title')} subheader={t('presentations.employers.subheader')} />
      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('presentations.employers.employer')}</TableCell>
              <TableCell>{t('presentations.employers.replacement')}</TableCell>
              <TableCell>{t('presentations.employers.updated')}</TableCell>
              {canManage && <TableCell align="right" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.employer}</TableCell>
                <TableCell>{row.replacement}</TableCell>
                <TableCell>{[fDate(row.updated_at), [row.updated_by?.first_name, row.updated_by?.last_name].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</TableCell>
                {canManage && (
                  <TableCell align="right">
                    <IconButton size="small" aria-label={t('presentations.employers.remove', { employer: row.employer })} onClick={() => handleRemove(row.id)}>
                      <Iconify icon="solar:trash-bin-trash-bold" width={18} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && !isFetching && (
              <TableRow><TableCell colSpan={4}>{t('presentations.employers.empty')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
